import React, { FC, useMemo } from 'react';

import MapItWidget, { IMapPoint, IMapStatusInfo } from './index';

// Task 25/26/27/28 (client decision) — stop-wise Map-It: always renders ONE joined route,
// Stop 1 → ... → Final, with a dot marker + hover tooltip at every middle stop. The former
// per-leg dropdown (Stop 1 → Stop 2, Stop 2 → Stop 3, ...) has been removed per client decision —
// the client only wants the single master route, never an individual-leg view. NEW component —
// MapItWidget (./index.tsx) is reused as-is for the actual map rendering; it only gained one small
// additive prop (showMidpointMarkers) so this component can show dots at each waypoint — every
// other MapItWidget caller is unaffected since it defaults false. Works for classic 2-stop quotes
// too (no middle stops, so no dots) so this is a drop-in replacement for the old single-route mount.
export interface IMultiStopMapStop {
    position: number;
    city: string | null;
    state?: string | null;
    lat: number | null;
    lng: number | null;
    // Task 28 (client decision) — pickup/drop counts shown in the stop marker's hover tooltip on
    // the Master Route view (see toMapPoint/toWaypointPoint below and MapBoxMap.tsx's
    // updateMidpointMarkers). Sourced from the quote's multiStopSummary (QuoteDetail/index.tsx).
    unitsOn?: number;
    unitsOff?: number;
}

// Extends IMapPoint with the tooltip detail — only waypoints (middle stops) need this; start/end
// points reuse plain IMapPoint since they're Stop 1/Final, not the "stop" the tooltip is about.
type IWaypointPoint = IMapPoint & { position?: number; unitsOn?: number; unitsOff?: number };

// onOpenClick reports back the first/last stop positions of the route on screen, so the caller
// (QuoteDetail/index.tsx) can resolve the matching addresses instead of hardcoding shipper/consignee.
interface ISelectedRoutePositions {
    fromPosition: number;
    toPosition: number;
}

interface IMultiStopMapItWidgetProps {
    stops: IMultiStopMapStop[];
    title?: string;
    openButtonLabel?: string;
    onOpenClick?: (selectedRoute: ISelectedRoutePositions) => void;
    preventCrossBorder?: boolean;
    statusInfo?: IMapStatusInfo;
    isLoading?: boolean;
}

const toMapPoint = (stop?: IMultiStopMapStop | null): IMapPoint | null => {
    if (!stop || stop.lat === null || stop.lat === undefined || stop.lng === null || stop.lng === undefined) return null;

    return {
        lat: stop.lat,
        lng: stop.lng,
        name: stop.city ? [stop.city, stop.state].filter(Boolean).join(', ') : null,
    };
};

const toWaypointPoint = (stop?: IMultiStopMapStop | null): IWaypointPoint | null => {
    const point = toMapPoint(stop);

    if (!point) return null;

    return { ...point, position: stop?.position, unitsOn: stop?.unitsOn, unitsOff: stop?.unitsOff };
};

const MultiStopMapItWidget: FC<IMultiStopMapItWidgetProps> = ({ stops, title, openButtonLabel, onOpenClick, preventCrossBorder, statusInfo, isLoading }) => {
    const sortedStops = useMemo(() => [...stops].sort((a, b) => a.position - b.position), [stops]);

    // Always the full joined route: Stop 1 -> ... -> Final, one line, with every stop strictly
    // between the first and last shown as a dot marker (Task 27) with a pickup/drop hover tooltip
    // (Task 28). No leg-by-leg view — client decision.
    const { startPoint, endPoint, waypoints, fromPosition, toPosition } = useMemo(() => {
        const middleWaypoints = sortedStops
            .slice(1, -1)
            .map(toWaypointPoint)
            .filter((point): point is IWaypointPoint => !!point);

        return {
            startPoint: toMapPoint(sortedStops[0]),
            endPoint: toMapPoint(sortedStops[sortedStops.length - 1]),
            waypoints: middleWaypoints,
            fromPosition: sortedStops[0]?.position,
            toPosition: sortedStops[sortedStops.length - 1]?.position,
        };
    }, [sortedStops]);

    return (
        <MapItWidget
            startPoint={startPoint}
            endPoint={endPoint}
            waypoints={waypoints}
            title={title}
            preventCrossBorder={preventCrossBorder}
            showOpenButton={!!onOpenClick}
            onOpenClick={onOpenClick && fromPosition !== undefined && toPosition !== undefined ? () => onOpenClick({ fromPosition, toPosition }) : undefined}
            openButtonLabel={openButtonLabel}
            statusInfo={statusInfo}
            hideStatusInfo
            isLoading={isLoading}
            showMidpointMarkers
        />
    );
};

export default MultiStopMapItWidget;
